import { Button } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { FC, FormEvent, ReactNode } from "react";
import { FormDrawerUnsavedBadge } from "./FormDrawerUnsavedBadge";

export type FormDrawerSize = "sm" | "md" | "lg";

// px; the Drawer itself caps the width to the viewport on smaller screens
const PAPER_WIDTH: Record<FormDrawerSize, number> = {
  sm: 480,
  md: 720,
  lg: 900,
};

type Props = {
  readonly open: boolean;
  readonly size?: FormDrawerSize;
  readonly title: string;
  readonly submitLabel: string;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting?: boolean;
  readonly isSubmitDisabled?: boolean;
  /** Unsaved changes across the whole drawer, shown next to the buttons. */
  readonly unsavedCount?: number;
  readonly navigation?: ReactNode;
  readonly children?: ReactNode;
};

export const FormDrawer: FC<Props> = ({
  open,
  size = "md",
  title,
  submitLabel,
  onClose,
  onSubmit,
  isSubmitting = false,
  isSubmitDisabled = false,
  unsavedCount = 0,
  navigation,
  children,
}) => {
  const { t } = useResource();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // React bubbles events through portals, so a form higher up in the component tree would submit as well
    event.stopPropagation();
    if (isSubmitting || isSubmitDisabled) {
      return;
    }
    onSubmit();
  };

  return (
    // the package Drawer: navigation scrolls with the grey work area, and it doesn't close while submitting
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(Resources.Common.Close)}
      title={title}
      onSubmit={handleSubmit}
      width={PAPER_WIDTH[size]}
      isPending={isSubmitting}
      navigation={navigation}
      startAction={unsavedCount > 0 && <FormDrawerUnsavedBadge count={unsavedCount} />}
      primaryAction={
        <Button variant="contained" type="submit" loading={isSubmitting} disabled={isSubmitDisabled}>
          {submitLabel}
        </Button>
      }
    >
      {children}
    </Drawer>
  );
};
